import { FC, useEffect } from 'react';
import { createZodFormResolver, useFormControls } from '@mews/framework';
import { Interval } from '@mews-commander/types';
import { NewReservationFormKeys, newReservationFormSchema, NewReservationFormValues } from '../schema';
import { useNewReservationFormDefaultValues } from '../utils/useNewReservationFormDefaultValues';
import { useNewReservationDateTimeBoundaries } from '../utils/useNewReservationDateTimeBoundaries';
import { useNewReservationBookablePeriodsDiffs } from '../utils/useNewReservationBookablePeriodsDiffs';
import { NewReservationPanel, NewReservationPanelProps } from './NewReservationPanel';

export interface NewReservationPanelFormWrapperProps
    extends Omit<NewReservationPanelProps, 'onSubmit' | 'dateTimeBoundaries' | 'bookablePeriodsDiffs'>,
        Interval {
    defaultRateId: string | undefined;
    onSubmit: (values: NewReservationFormValues) => void;
    isDayUse: boolean;
}

export const NewReservationPanelFormWrapper: FC<NewReservationPanelFormWrapperProps> = ({
    startUtc,
    endUtc,
    resourceCategory,
    defaultRateId,
    timeUnitPeriod,
    ageCategories,
    resource,
    isDayUse,
    ratesOptions,
    onClose,
    onSubmit,
    ...others
}) => {
    const defaultValues = useNewReservationFormDefaultValues({
        ageCategories,
        resourceCategoryCapacity: resourceCategory?.capacity ?? 0,
        startUtc,
        endUtc,
        defaultRateId,
        ratesOptions,
    });

    const { renderFormContent, reset, submit, watch } = useFormControls<NewReservationFormValues>({
        dataTestForm: 'new-reservation-form',
        defaultValues,
        onSubmit,
        resolver: createZodFormResolver(newReservationFormSchema),
    });

    const {
        [NewReservationFormKeys.ArrivalDate]: arrivalDate,
        [NewReservationFormKeys.DepartureDate]: departureDate,
        [NewReservationFormKeys.ArrivalHours]: arrivalHours,
        [NewReservationFormKeys.DepartureHours]: departureHours,
    } = watch();

    useEffect(() => {
        if (resourceCategory) {
            reset(defaultValues, { keepErrors: true, keepIsValid: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startUtc, resourceCategory, resource, timeUnitPeriod, defaultRateId, reset]);

    const dateTimeBoundaries = useNewReservationDateTimeBoundaries(
        arrivalDate,
        departureDate,
        arrivalHours,
        departureHours,
        timeUnitPeriod
    );

    const bookablePeriodsDiffs = useNewReservationBookablePeriodsDiffs(
        arrivalDate,
        departureDate,
        arrivalHours,
        departureHours,
        isDayUse
    );

    return renderFormContent(
        <NewReservationPanel
            {...others}
            ageCategories={ageCategories}
            timeUnitPeriod={timeUnitPeriod}
            resourceCategory={resourceCategory}
            resource={resource}
            onSubmit={submit}
            onClose={onClose}
            dateTimeBoundaries={dateTimeBoundaries}
            bookablePeriodsDiffs={bookablePeriodsDiffs}
            ratesOptions={ratesOptions}
        />
    );
};
